import moment from 'moment';
import { SWRConfiguration } from 'swr';

import {
  AbsencesReportData,
  AbsencesReportLimitedData,
  AnalyticsReportParams,
  AnalyticsReportProps,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypeConfig,
  AnalyticsReportTypes,
  AnniversariesReportData,
  BirthdaysReportData,
  CompensationEqualityReportData,
  CustomDataReportData,
  DynamicPersonnelReportData,
  EmployeeEquipmentReportData,
  EmployeesCostCenterReportData,
  ExpiringDocumentsReportData,
  FTEReportData,
  FormerAndNewEmployeesReportData,
  HeadCountReportData,
  OverrideManagerReportData,
  PersonnelContactsReportData,
  PersonnelListReportData,
  PersonnelReportData,
  PositionChangesReportData,
  RolesListReportData,
  SubstituteManagerReportData,
  VacantionBalancesReportData,
  VacationsReportData,
  WorkExperienceReportData,
} from '~/data/reports/types';
import { UseArraySWROutput, useArraySWR } from '~/hooks/useSWR';
import { fetchFromOldApi } from '~/services/Http';

enum AnalyticsReport {
  AbsencesReport = 'absencelist',
  AbsencesReportLimited = 'absencelistlimited',
  AnniversariesReport = 'workanniversary',
  BirthdaysReport = 'birthday',
  CompensationEqualityReport = 'equality/compensation',
  CustomDataReport = 'customdata',
  DynamicPersonnel = 'dynamicpersonnel',
  EmployeeEquipmentReport = 'equipment',
  EmployeesCostCenterReport = 'costcenter',
  EmployeesReport = 'employeelist',
  ExpiringDocumentsReport = 'expiringdocument',
  FTEReport = 'fte',
  HeadCountReport = 'headcountsummary',
  OverrideManagerReport = 'overridemanager',
  PersonnelContacts = 'contact',
  PersonnelList = 'personnellist',
  PositionChangesReport = 'positionchange',
  RolesList = 'roleresponsibility',
  SubstituteManager = 'orgsubresp',
  VacationBalancesReport = 'absencevacationlist',
  VacationsReport = 'vacation',
  WorkExperienceReport = 'workexperiencesummary',
  PersonnelReport = 'personnelfull',
}

const getReportURLParams = <P>(
  props?: AnalyticsReportParams<P>,
): string | undefined => {
  return !!props
    ? Object.entries(props)
        .filter(([_key, value]) => typeof value !== 'undefined')
        .map((keyValuePair) => {
          const [key, value] = keyValuePair;

          if (moment(value).isValid()) {
            const formattedDate: string = !!value
              ? moment(value as Date).format('YYYY-MM-DD')
              : '';

            return `${key}=${formattedDate}`;
          } else {
            return keyValuePair.join('=');
          }
        })
        .join('&')
    : undefined;
};

const getReportURL = (props: {
  report: AnalyticsReport;
  reportType: AnalyticsReportTypeConfig;
  params?: string;
  abbreviateManagerURL?: boolean;
}) => {
  const { reportType: config, report, params, abbreviateManagerURL } = props;

  const base = '/json/report';
  const reportParams = params ? `${report}?${params}` : report;

  switch (config.type) {
    case AnalyticsReportTypes.Company:
      return `${base}/${config.type}/-1/all/${reportParams}`;
    case AnalyticsReportTypes.Unit:
      return `${base}/${config.type}/${config.unitId}/${config.scope}/${reportParams}`;
    case AnalyticsReportTypes.Team: {
      const role =
        abbreviateManagerURL && config.role === AnalyticsReportRoleScope.Manager
          ? 'mgr'
          : config.role;

      return `${base}/${role}/${config.employeeId}/${config.scope}/${reportParams}`;
    }
    case AnalyticsReportTypes.HR:
      return `${base}/${AnalyticsReportTypes.HR}/${config.employeeId}/${AnalyticsReportScope.All}/${reportParams}`;
    case AnalyticsReportTypes.Employee:
      return `${base}/${config.type}/${config.employeeId}/direct/${reportParams}`;
    case AnalyticsReportTypes.LegalEntity:
      return `${base}/${config.type}/${config.legalEntityId}/direct/${reportParams}`;
    default:
      return undefined;
  }
};

const getAnalyticsReportData = <T>(url: string): Promise<T[]> => {
  return fetchFromOldApi(url).then((r): Promise<T[]> => r.json());
};

const useAnalyticsReports = <T, P>(
  report: AnalyticsReport,
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
  reportConfig?: {
    abbreviateManagerURL?: boolean;
  },
): UseArraySWROutput<T> => {
  const reportSWRConfig: SWRConfiguration = !!swrConfig
    ? swrConfig
    : {
        revalidateOnFocus: false,
        revalidateIfStale: true, // Requesting with same params, should by default refresh Report Data
      };

  const { reportType, params, reportDisabled } = props;

  const reportUrlParams = getReportURLParams(params);

  const reportUrl = getReportURL({
    report,
    reportType,
    params: reportUrlParams,
    abbreviateManagerURL: reportConfig?.abbreviateManagerURL,
  });

  return useArraySWR(
    !!reportUrl && !reportDisabled
      ? [`ANALYTICS_REPORT_${report}`, reportUrl]
      : null,
    ([_key, reportUrl]: [string, string]) => {
      return getAnalyticsReportData(reportUrl);
    },
    reportSWRConfig,
  );
};

/************* People *************/

export const usePersonnelReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<PersonnelReportData, P>(
    AnalyticsReport.PersonnelReport,
    props,
    swrConfig,
  );

export const useHeadCountReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<HeadCountReportData, P>(
    AnalyticsReport.HeadCountReport,
    props,
    swrConfig,
  );

export const useFTEReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<FTEReportData, P>(
    AnalyticsReport.FTEReport,
    props,
    swrConfig,
  );

export const usePersonnelListReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<PersonnelListReportData, P>(
    AnalyticsReport.PersonnelList,
    props,
    swrConfig,
  );

// DynamicPersonnelReportData
export enum DynamicPersonnelIncludeOptionalData {
  WorkingHours = 'wh',
  TotalCompensation = 'totalComp',
  Salaries = 'salaries',
  AdditionalPersonnelGroups = 'addlPG',
  AdditionalTrees = 'addlTree',
  UserAccountDetails = 'account',
}

export type DynamicPersonnelCustomParams = {
  [key in DynamicPersonnelIncludeOptionalData]?: boolean;
};

export const useDynamicPersonnelReportData = <DynamicPersonnelCustomParams>(
  props: AnalyticsReportProps<DynamicPersonnelCustomParams>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<DynamicPersonnelReportData, DynamicPersonnelCustomParams>(
    AnalyticsReport.DynamicPersonnel,
    props,
    swrConfig,
  );

export const usePersonnelContactsReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<PersonnelContactsReportData, P>(
    AnalyticsReport.PersonnelContacts,
    props,
    swrConfig,
  );

export const useExpiringDocumentsReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<ExpiringDocumentsReportData, P>(
    AnalyticsReport.ExpiringDocumentsReport,
    props,
    swrConfig,
  );

export const useSubstituteManagerReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<SubstituteManagerReportData, P>(
    AnalyticsReport.SubstituteManager,
    props,
    swrConfig,
  );

export const useRolesListReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<RolesListReportData, P>(
    AnalyticsReport.RolesList,
    props,
    swrConfig,
  );

export const useAbsencesReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<AbsencesReportData, P>(
    AnalyticsReport.AbsencesReport,
    props,
    swrConfig,
  );

export const useAbsencesReportLimitedData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<AbsencesReportLimitedData, P>(
    AnalyticsReport.AbsencesReportLimited,
    props,
    swrConfig,
  );

export const useVacationsReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<VacationsReportData, P>(
    AnalyticsReport.VacationsReport,
    props,
    swrConfig,
  );

export const useVacationBalancesReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<VacantionBalancesReportData, P>(
    AnalyticsReport.VacationBalancesReport,
    props,
    swrConfig,
  );

export const useBirthdaysReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<BirthdaysReportData, P>(
    AnalyticsReport.BirthdaysReport,
    props,
    swrConfig,
  );

export const useAnniversariesReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<AnniversariesReportData, P>(
    AnalyticsReport.AnniversariesReport,
    props,
    swrConfig,
  );

export const useFormerAndNewEmployeesReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<FormerAndNewEmployeesReportData, P>(
    AnalyticsReport.EmployeesReport,
    props,
    swrConfig,
  );

export const useEmployeeEquipmentReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<EmployeeEquipmentReportData, P>(
    AnalyticsReport.EmployeeEquipmentReport,
    props,
    swrConfig,
  );

export const useCustomDataReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<CustomDataReportData, P>(
    AnalyticsReport.CustomDataReport,
    props,
    swrConfig,
  );

export const usePositionChangesReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<PositionChangesReportData, P>(
    AnalyticsReport.PositionChangesReport,
    props,
    swrConfig,
  );

export const useOverrideManagerReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<OverrideManagerReportData, P>(
    AnalyticsReport.OverrideManagerReport,
    props,
    swrConfig,
  );

export const useEmployeesCostCenterData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<EmployeesCostCenterReportData, P>(
    AnalyticsReport.EmployeesCostCenterReport,
    props,
    swrConfig,
  );

// WorkExperienceReportData
export enum WorkExperienceOptionalSettings {
  IncludeCompanyHistory = 'incCompanyHistory',
  UseOriginalStartDate = 'useOriginalStartDate',
  IncludeExternalExperienceAndLongAbsences = 'extExperience',
  DeductLongAbsences = 'deductAbsences',
}

export type WorkExperienceReportCustomParams = {
  [key in WorkExperienceOptionalSettings]?: boolean;
};

export const useWorkExperienceReportData = <WorkExperienceReportCustomParams>(
  props: AnalyticsReportProps<WorkExperienceReportCustomParams>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<
    WorkExperienceReportData,
    WorkExperienceReportCustomParams
  >(AnalyticsReport.WorkExperienceReport, props, swrConfig);

/************* Rewarding *************/

export const useCompensationEqualityReportData = <P>(
  props: AnalyticsReportProps<P>,
  swrConfig?: SWRConfiguration,
) =>
  useAnalyticsReports<CompensationEqualityReportData, P>(
    AnalyticsReport.CompensationEqualityReport,
    props,
    swrConfig,
  );
